import { createTheme, ThemeOptions } from '@mui/material/styles';
import '@fontsource/inter-tight/variable.css';

type PaletteMode = 'light' | 'dark';

// Farbpalette für Light Mode
const lightColors = {
  primary: {
    main: '#FFFFFF',
    light: '#534bae',
    dark: '#000000',
    blue: '#0F1F44',
    contrastText: '#1a237e',
    yellow: '#fcbc0d',
  },
  secondary: {
    main: '#f50057',
    light: '#ff5983',
    dark: '#bb002f',
    contrastText: '#ffffff',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#ffffff',
  },
  background: {
    default: '#ffffff',
    paper: '#ffffff',
  },
  text: {
    primary: '#333333',
    blue: '#000051',
    yellow: '#fcbc0d',
    secondary: '#666666',
    gray: '#828282',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
  },
  divider: '#e0e0e0',
};

// Farbpalette für Dark Mode
const darkColors = {
  primary: {
    main: '#1e1e1e',
    light: '#7986cb',
    dark: '#ffffff',
    blue: '#5c7cfa',
    contrastText: '#90caf9',
    yellow: '#ffd54f',
  },
  secondary: {
    main: '#ff4081',
    light: '#ff79b0',
    dark: '#c60055',
    contrastText: '#000000',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
  },
  success: {
    main: '#66bb6a',
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#000000',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
  },
  text: {
    primary: '#ffffff',
    blue: '#90caf9',
    yellow: '#ffd54f',
    secondary: '#b0b0b0',
    gray: '#9e9e9e',
  },
  grey: {
    50: '#303030',
    100: '#424242',
    200: '#4a4a4a',
    300: '#5a5a5a',
  },
  divider: '#424242',
};

// Typografie-Einstellungen
const typography = {
  htmlFontSize: 16,
  fontSize: 16,
  fontFamily: '"Inter Tight", sans-serif',
  h4: {
    fontWeight: 500,
    margin: 0,
    fontSize: '2.1875rem',
  },
  h6: {
    fontWeight: 500,
    fontSize: '1.125rem',
  },
  h5: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  subtitle1: {
    fontWeight: 500,
  },
};

// Komponenten-spezifische Überschreibungen
const getComponents = (mode: PaletteMode) => {
  const colors = mode === 'dark' ? darkColors : lightColors;
  const isDark = mode === 'dark';

  return {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? '#1e1e1e' : colors.primary.main,
          '& .MuiTypography-root': {
            color: isDark ? '#ffffff' : colors.primary.dark,
          },
          '& .MuiIconButton-root': {
            color: isDark ? '#ffffff' : colors.primary.dark,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
        },
        contained: {
          backgroundColor: isDark ? '#5c7cfa' : '#0F1F44',
          boxShadow: 'none',
          color: '#FFFFFF',
          padding: '0.5rem 1.5rem',
          '&:hover': {
            backgroundColor: isDark ? '#ffd54f' : '#fcbc0d',
            color: isDark ? '#1e1e1e' : '#0F1F44',
            boxShadow: 'none',
          },
        },
      },
      defaultProps: {},
    },
    MuiContainer: {
      defaultProps: {
        disableGutters: true,
        maxWidth: false as const,
      },
      styleOverrides: {
        root: {
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          overflow: 'hidden',
          margin: 0,
          padding: 0,
          height: '100vh',
        },
        '#root': {
          height: '100vh',
          display: 'flex',
          flexDirection: 'column' as const,
        },
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: isDark ? '#2a2a2a' : '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: isDark ? '#555555' : '#c1c1c1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: isDark ? '#666666' : '#a8a8a8',
        },
      },
    },
    MuiGrid: {
      defaultProps: {
        spacing: 0,
      },
      styleOverrides: {
        root: {
          margin: 0,
        },
        item: {
          padding: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '70%',
          aspectRatio: '1 / 1',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: isDark ? '#5c7cfa' : '#0F1F44',
          '&.Mui-checked': {
            color: isDark ? '#5c7cfa' : '#0F1F44',
          },
          padding: 2,
          fontSize: '1.1rem',
          '& .MuiSvgIcon-root': {
            fontSize: '1.1rem',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
          },
          // Invert dark icons to be visible in dark mode, but preserve colored icons
          '& img:not(.preserve-color)': {
            filter: isDark ? 'brightness(0) invert(1)' : 'none',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? '#424242' : '#333333',
          color: '#ffffff',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.divider,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.divider,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          ...(isDark && {
            '&.MuiAlert-standardInfo': {
              backgroundColor: 'rgba(33, 150, 243, 0.15)',
              color: '#90caf9',
              '& .MuiAlert-icon': { color: '#90caf9' },
              '& .MuiAlert-action': { color: '#90caf9' },
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              color: '#81c784',
              '& .MuiAlert-icon': { color: '#81c784' },
              '& .MuiAlert-action': { color: '#81c784' },
            },
            '&.MuiAlert-standardWarning': {
              backgroundColor: 'rgba(255, 152, 0, 0.15)',
              color: '#ffb74d',
              '& .MuiAlert-icon': { color: '#ffb74d' },
              '& .MuiAlert-action': { color: '#ffb74d' },
            },
            '&.MuiAlert-standardError': {
              backgroundColor: 'rgba(244, 67, 54, 0.15)',
              color: '#e57373',
              '& .MuiAlert-icon': { color: '#e57373' },
              '& .MuiAlert-action': { color: '#e57373' },
            },
            // Filled variants für Dark Mode
            '&.MuiAlert-filledInfo': {
              '& .MuiAlert-icon': { color: '#ffffff' },
              '& .MuiAlert-action': { color: '#ffffff' },
            },
            '&.MuiAlert-filledSuccess': {
              '& .MuiAlert-icon': { color: '#ffffff' },
              '& .MuiAlert-action': { color: '#ffffff' },
            },
            '&.MuiAlert-filledWarning': {
              '& .MuiAlert-icon': { color: '#ffffff' },
              '& .MuiAlert-action': { color: '#ffffff' },
            },
            '&.MuiAlert-filledError': {
              '& .MuiAlert-icon': { color: '#ffffff' },
              '& .MuiAlert-action': { color: '#ffffff' },
            },
          }),
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          ...(isDark && {
            borderColor: 'rgba(255, 255, 255, 0.23)',
          }),
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? '#5c7cfa' : '#0F1F44',
          },
        },
        icon: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-focused': {
            color: isDark ? '#5c7cfa' : '#0F1F44',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? '#5c7cfa' : '#0F1F44',
          },
        },
        input: {
          color: colors.text.primary,
        },
      },
    },
  };
};

// Gemeinsame Drag-and-Drop-Styles für einheitliches Design
export const getDragDropStyles = (isDark: boolean) => ({
  container: (isDragOver: boolean, message: string = "Dateien hier hineinziehen") => ({
    position: 'relative' as const,
    ...(isDragOver && {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDark ? 'rgba(92, 124, 250, 0.15)' : 'rgba(15, 31, 68, 0.08)',
        border: `2px dashed ${isDark ? '#5c7cfa' : '#0F1F44'}`,
        borderRadius: '4px',
        zIndex: 10,
        pointerEvents: 'none'
      },
      '&::after': {
        content: `"${message}"`,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: isDark ? '#2a2a2a' : 'white',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)',
        fontSize: '1.1rem',
        color: isDark ? '#ffffff' : '#0F1F44',
        fontWeight: 500,
        zIndex: 11,
        pointerEvents: 'none',
        whiteSpace: 'nowrap'
      }
    })
  }),
  processing: {
    opacity: 0.7,
    pointerEvents: 'none' as const
  },
  messages: {
    zipOnly: "📦 Nur ZIP-Dateien hier hineinziehen",
    documentsOnly: "📄 Nur PDF-, DOCX- und TXT-Dateien hier hineinziehen",
    folders: "📁 Ordner hier hineinziehen",
    foldersForServices: "📁 Ordner hier hineinziehen um Dienstleistungen zu erstellen",
    addDocuments: "📄 PDF-, DOCX- und TXT-Dateien hier hineinziehen um sie hinzuzufügen",
    generic: "📄 Dateien hier hineinziehen"
  }
});

// Legacy export für Rückwärtskompatibilität
export const dragDropStyles = getDragDropStyles(false);

// Theme erstellen basierend auf Modus
export const getTheme = (mode: PaletteMode) => {
  const colors = mode === 'dark' ? darkColors : lightColors;

  return createTheme({
    palette: {
      mode,
      ...colors,
    },
    typography,
    components: getComponents(mode),
    shape: {
      borderRadius: 8,
    },
  } as ThemeOptions);
};

// Default Theme (Light) für Rückwärtskompatibilität
const theme = getTheme('light');

export default theme;
